// src/Studio/masks/gradientRenderer.ts

import type { GradientFade } from '../types/gradientFade';

/**
 * Render a linear or radial gradient alpha mask to a canvas
 * White = fully visible, Black = fully transparent
 */
export function renderGradientToCanvas(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  gradient: GradientFade,
  elementWidth: number,
  elementHeight: number
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Ensure canvas is correct size
  if (canvas.width !== elementWidth || canvas.height !== elementHeight) {
    canvas.width = elementWidth;
    canvas.height = elementHeight;
  }

  // Clear canvas to black (fully transparent in mask context)
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, elementWidth, elementHeight);

  if (gradient.type === 'linear') {
    renderLinearGradient(ctx, gradient, elementWidth, elementHeight);
  } else {
    renderRadialGradient(ctx, gradient, elementWidth, elementHeight);
  }
}

function renderLinearGradient(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  gradient: GradientFade,
  width: number,
  height: number
): void {
  const { angle, offsetX, offsetY, strength, softness, invert } = gradient;

  // Convert angle to radians (0° = top to bottom, 90° = left to right)
  const angleRad = ((angle - 90) * Math.PI) / 180;

  // Calculate center point with offset
  const cx = width / 2 + (offsetX / 100) * width;
  const cy = height / 2 + (offsetY / 100) * height;

  // Calculate gradient line length based on element diagonal
  const diagonal = Math.sqrt(width * width + height * height);
  const gradientLength = diagonal;

  // Calculate start and end points
  const dx = Math.cos(angleRad) * gradientLength / 2;
  const dy = Math.sin(angleRad) * gradientLength / 2;

  const x0 = cx - dx;
  const y0 = cy - dy;
  const x1 = cx + dx;
  const y1 = cy + dy;

  // Create gradient
  const canvasGradient = ctx.createLinearGradient(x0, y0, x1, y1);

  // Calculate stop positions based on softness
  // softness 0 = hard edge at 50%, softness 100 = gradient spans full length
  const softnessRatio = softness / 100;
  const hardEdge = 0.5;
  const transitionStart = hardEdge - softnessRatio * 0.5;
  const transitionEnd = hardEdge + softnessRatio * 0.5;

  // Apply strength (controls overall opacity range)
  const strengthRatio = strength / 100;
  const opaqueColor = `rgba(255, 255, 255, ${strengthRatio})`;
  const transparentColor = 'rgba(0, 0, 0, 0)';

  if (invert) {
    canvasGradient.addColorStop(0, opaqueColor);
    canvasGradient.addColorStop(Math.max(0, transitionStart), opaqueColor);
    canvasGradient.addColorStop(Math.min(1, transitionEnd), transparentColor);
    canvasGradient.addColorStop(1, transparentColor);
  } else {
    canvasGradient.addColorStop(0, transparentColor);
    canvasGradient.addColorStop(Math.max(0, transitionStart), transparentColor);
    canvasGradient.addColorStop(Math.min(1, transitionEnd), opaqueColor);
    canvasGradient.addColorStop(1, opaqueColor);
  }

  ctx.fillStyle = canvasGradient;
  ctx.fillRect(0, 0, width, height);
}

function renderRadialGradient(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  gradient: GradientFade,
  width: number,
  height: number
): void {
  const { centerX, centerY, radius, strength, softness, invert } = gradient;

  // Calculate center point
  const cx = (centerX / 100) * width;
  const cy = (centerY / 100) * height;

  // Calculate radius (percentage of smaller dimension)
  const minDim = Math.min(width, height);
  const r = (radius / 100) * minDim;

  // Create radial gradient
  const canvasGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);

  // Calculate transition based on softness
  const softnessRatio = softness / 100;
  const innerRadius = 1 - softnessRatio;

  // Apply strength
  const strengthRatio = strength / 100;
  const opaqueColor = `rgba(255, 255, 255, ${strengthRatio})`;
  const transparentColor = 'rgba(0, 0, 0, 0)';

  if (invert) {
    // Center is transparent, edge is opaque
    canvasGradient.addColorStop(0, transparentColor);
    canvasGradient.addColorStop(Math.max(0, innerRadius), transparentColor);
    canvasGradient.addColorStop(1, opaqueColor);
  } else {
    // Center is opaque, edge is transparent
    canvasGradient.addColorStop(0, opaqueColor);
    canvasGradient.addColorStop(Math.max(0, innerRadius), opaqueColor);
    canvasGradient.addColorStop(1, transparentColor);
  }

  ctx.fillStyle = canvasGradient;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Get gradient handle positions for on-canvas editing
 * Returns positions in element-local coordinates
 */
export function getGradientHandles(
  gradient: GradientFade,
  elementWidth: number,
  elementHeight: number
): { start?: { x: number; y: number }; end?: { x: number; y: number }; center?: { x: number; y: number }; radius?: { x: number; y: number } } {
  if (gradient.type === 'linear') {
    const angleRad = ((gradient.angle - 90) * Math.PI) / 180;
    const cx = elementWidth / 2 + (gradient.offsetX / 100) * elementWidth;
    const cy = elementHeight / 2 + (gradient.offsetY / 100) * elementHeight;
    const len = Math.min(elementWidth, elementHeight) * 0.4;

    return {
      start: {
        x: cx - Math.cos(angleRad) * len,
        y: cy - Math.sin(angleRad) * len,
      },
      end: {
        x: cx + Math.cos(angleRad) * len,
        y: cy + Math.sin(angleRad) * len,
      },
    };
  } else {
    const cx = (gradient.centerX / 100) * elementWidth;
    const cy = (gradient.centerY / 100) * elementHeight;
    const r = (gradient.radius / 100) * Math.min(elementWidth, elementHeight);

    return {
      center: { x: cx, y: cy },
      radius: { x: cx + r, y: cy },
    };
  }
}

/**
 * Update gradient from handle drag
 */
export function updateGradientFromHandles(
  gradient: GradientFade,
  handleType: 'start' | 'end' | 'center' | 'radius',
  x: number,
  y: number,
  elementWidth: number,
  elementHeight: number
): Partial<GradientFade> {
  if (gradient.type === 'linear') {
    const handles = getGradientHandles(gradient, elementWidth, elementHeight);

    if (handleType === 'start' || handleType === 'end') {
      const other = handleType === 'start' ? handles.end! : handles.start!;
      const current = { x, y };

      // Calculate new angle from the two points
      const dx = (handleType === 'end' ? current.x : other.x) - (handleType === 'start' ? current.x : other.x);
      const dy = (handleType === 'end' ? current.y : other.y) - (handleType === 'start' ? current.y : other.y);
      const angle = (Math.atan2(dy, dx) * 180 / Math.PI) + 90;

      // Calculate new center (midpoint)
      const cx = (current.x + other.x) / 2;
      const cy = (current.y + other.y) / 2;

      return {
        angle: ((angle % 360) + 360) % 360,
        offsetX: ((cx - elementWidth / 2) / elementWidth) * 100,
        offsetY: ((cy - elementHeight / 2) / elementHeight) * 100,
      };
    }
  } else {
    if (handleType === 'center') {
      return {
        centerX: (x / elementWidth) * 100,
        centerY: (y / elementHeight) * 100,
      };
    } else if (handleType === 'radius') {
      const cx = (gradient.centerX / 100) * elementWidth;
      const cy = (gradient.centerY / 100) * elementHeight;
      const r = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
      return {
        radius: (r / Math.min(elementWidth, elementHeight)) * 100,
      };
    }
  }

  return {};
}
